/**
 * Spec 004.5 PR1p — `WasmCpuEngineAdapter` body.
 *
 * Wraps `crates/slot-engine-wgpu/pkg/`'s `run_cpu` JS export.
 * Same SpinOutcome[] → MetricsResult fold as the WebGPU adapter
 * (see `webgpu.ts`); the difference is the wasm entry point + the
 * progress-event shape (CPU fires `(0, null)` / `(n, null)` per
 * the contract's "indeterminate" semantics, vs WebGPU's
 * `(done, total)` per-chunk events).
 */

import { aggregateMetrics } from "./aggregateMetrics";
import { allocateRun, abortRun, releaseRun } from "./runRegistry";
import {
  CancelledError,
  EngineError,
  decodeReplayedOutcomes,
  type EngineAdapter,
  type MetricsResult,
  type ProgressCallback,
  type ReplayedOutcome,
  type RunId,
  type SimulationRequest,
} from "./types";
import type { WasmModuleExports } from "./wasmModule";

export class WasmCpuEngineAdapter implements EngineAdapter {
  readonly mode = "cpu" as const;

  constructor(private readonly mod: WasmModuleExports) {}

  run(
    request: SimulationRequest,
    onProgress?: ProgressCallback,
  ): [RunId, Promise<MetricsResult>] {
    const { runId, numericId, signal } = allocateRun();
    const promise = this.executeRun(
      request,
      numericId,
      signal,
      onProgress,
    ).finally(() => {
      releaseRun(runId);
    });
    return [runId, promise];
  }

  cancel(runId: RunId): void {
    const entry = abortRun(runId);
    if (entry !== null) {
      this.mod.cancel_run(entry.numericId);
    }
  }

  async sampleWinnings(request: SimulationRequest): Promise<number[]> {
    return (await this.sampleOutcomes(request)).map((o) => o.winnings);
  }

  async sampleOutcomes(request: SimulationRequest): Promise<ReplayedOutcome[]> {
    const { runId, numericId } = allocateRun();
    try {
      const seedBig = parseSeedHex(request.seed);
      const specJson = JSON.stringify(request.config);
      const bytes = await this.mod.run_cpu(
        specJson,
        seedBig,
        request.nSpins,
        numericId,
        null,
      );
      return decodeReplayedOutcomes(bytes);
    } catch (err) {
      throw wrapWasmError(err);
    } finally {
      releaseRun(runId);
    }
  }

  private async executeRun(
    request: SimulationRequest,
    numericId: number,
    signal: AbortSignal,
    onProgress?: ProgressCallback,
  ): Promise<MetricsResult> {
    if (signal.aborted) {
      throw new CancelledError();
    }
    const seedBig = parseSeedHex(request.seed);
    const specJson = JSON.stringify(request.config);
    const wasmCall = this.mod.run_cpu(
      specJson,
      seedBig,
      request.nSpins,
      numericId,
      onProgress ?? null,
    );
    let bytes: Uint8Array;
    try {
      bytes = await wasmCall;
    } catch (err) {
      if (signal.aborted) {
        throw new CancelledError();
      }
      throw wrapWasmError(err);
    }
    const outcomes = decodeReplayedOutcomes(bytes);
    return aggregateMetrics(outcomes, request.nSpins);
  }
}

function parseSeedHex(seed: string): bigint {
  if (!/^[0-9a-f]{16}$/.test(seed)) {
    throw new EngineError(
      `Invalid seed: expected 16-char lowercase hex, got "${seed}"`,
      "ENGINE_VALIDATION_FAILED",
    );
  }
  return BigInt(`0x${seed}`);
}

function wrapWasmError(err: unknown): EngineError {
  const message = err instanceof Error ? err.message : String(err);
  if (message.toLowerCase().includes("cancelled")) {
    return new CancelledError();
  }
  return new EngineError(
    `Wasm dispatch failed: ${message}`,
    "ENGINE_ERROR",
    message,
  );
}
